import { API_CONFIG } from '../../config/api';
import { Role } from '../../types';
import { convertToPdf } from '../files/pdfConverter';
import { fileToBase64 } from '../files/fileConverter';
import axios from 'axios';

// NOTA: Esta chave será removida na Fase 3 quando o upload for migrado para o backend
// Por enquanto, mantemos temporariamente para não quebrar a funcionalidade
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;



// Define specific type for the webhook payload to match exactly what was there
// ...
// Define specific type for the webhook payload to match exactly what was there
interface WebhookPayload {
  token: string;
  papel: string;
  "gemini-file-url"?: string; // Opcional - agora gerado pelo backend
  tipo: "arquivo";
  base64: string;
  assunto: string;
  pre_analise: string;
}

export async function submitFinalAnalysis(
  base64Content: string,
  _geminiFileUrl: string, // Ignorado - mantido por compatibilidade, upload agora feito no backend
  role: Role,
  matter: string,
  preAnalysisResult: string
): Promise<{ analysis: string; ementa: string }> {

  let papel = role.name;
  if (role.name === "Parecerista Jurídico" || role.name === "Controlador Interno") {
    papel += " " + matter;
  }

  const payload: WebhookPayload = {
    token: API_CONFIG.TOKEN,
    papel: papel,
    // gemini-file-url removido - agora o backend faz o upload
    tipo: "arquivo",
    base64: base64Content,
    assunto: matter,
    pre_analise: preAnalysisResult
  };

  console.log('Submitting Payload to Backend:', JSON.stringify(payload, null, 2));

  return await sendToWebhook(payload);
}

interface AnalysisResponse {
  ementa: string;
  parecer: string;
}



export async function analyzeDocument(
  file: File,
  role: Role,
  matter: string
): Promise<{ analysis: string; ementa: string }> {
  // This function is kept for backward compatibility but the new flow uses granular functions.
  // If the user uses the old flow, it won't have the pre-analysis interaction.
  try {
    const pdfFile = await convertToPdf(file);
    const base64Content = await fileToBase64(pdfFile);

    let papel = role.name;
    if (role.name === "Parecerista Jurídico" || role.name === "Controlador Interno") {
      papel += " " + matter;
    }

    const geminiUploadUrl = await uploadToGemini(pdfFile);

    const payload: WebhookPayload = {
      token: API_CONFIG.TOKEN,
      papel: papel,
      "gemini-file-url": geminiUploadUrl,
      tipo: "arquivo",
      base64: base64Content,
      assunto: matter,
      pre_analise: ""
    };

    return await sendToWebhook(payload);
  } catch (error: any) {
    console.error('Error in document analysis:', error);
    throw new Error(error.message || 'Erro ao analisar documento');
  }
}

async function sendToWebhook(payload: WebhookPayload): Promise<{ analysis: string; ementa: string }> {
  console.log('Sending request to:', API_CONFIG.WEBHOOK_URL);

  // Validate payload before sending
  if (!API_CONFIG.WEBHOOK_URL) {
    throw new Error('URL do webhook não configurada. Verifique a variável VITE_API_WEBHOOK_URL');
  }

  if (!API_CONFIG.TOKEN) {
    throw new Error('Token da API não configurado. Verifique a variável VITE_API_TOKEN');
  }

  // Log payload size for debugging (without exposing sensitive data)
  console.log('Payload validation:', {
    hasToken: !!payload.token,
    papel: payload.papel,
    tipo: payload.tipo,
    base64Length: payload.base64?.length,
    assunto: payload.assunto,
    preAnaliseLength: payload.pre_analise?.length
  });

  const response = await fetch(API_CONFIG.WEBHOOK_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    let errorText = '';
    let errorData: any = null;

    try {
      errorText = await response.text();
      errorData = JSON.parse(errorText);
    } catch {
      // errorText já contém o texto bruto
    }

    console.error('API Error Details:', {
      status: response.status,
      statusText: response.statusText,
      errorText,
      errorData,
      headers: Object.fromEntries(response.headers.entries())
    });

    // Try to extract meaningful error message
    const errorMessage = errorData?.message || errorData?.error || errorText || `Erro ${response.status}`;

    throw new Error(`Erro no servidor (${response.status}): ${errorMessage}`);
  }

  const data = await response.json();

  console.log('Response received:', {
    isArray: Array.isArray(data),
    length: Array.isArray(data) ? data.length : 'N/A',
    hasData: !!data
  });

  if (!Array.isArray(data) || data.length === 0) {
    console.error('Invalid response structure:', data);
    throw new Error('Resposta inválida do servidor: esperado array com resultados');
  }

  const analysisResponse = data[0] as AnalysisResponse;

  if (!analysisResponse?.parecer || !analysisResponse?.ementa) {
    console.error('Missing required fields in response:', {
      hasParecer: !!analysisResponse?.parecer,
      hasEmenta: !!analysisResponse?.ementa,
      keys: Object.keys(analysisResponse || {})
    });
    throw new Error('Resposta inválida: campos parecer ou ementa ausentes');
  }

  return {
    analysis: analysisResponse.parecer,
    ementa: analysisResponse.ementa
  };
}

export async function uploadToGemini(file: File): Promise<string> {
  // Usa a constante definida no topo do arquivo
  const BASE_URL = "https://generativelanguage.googleapis.com";
  const DISPLAY_NAME = file.name;

  const mimeType = file.type;
  const arrayBuffer = await file.arrayBuffer();
  const numBytes = arrayBuffer.byteLength;

  const uploadUrlResponse = await axios.post(
    `${BASE_URL}/upload/v1beta/files?key=${GOOGLE_API_KEY}`,
    {
      file: { display_name: DISPLAY_NAME },
    },
    {
      headers: {
        "X-Goog-Upload-Protocol": "resumable",
        "X-Goog-Upload-Command": "start",
        "X-Goog-Upload-Header-Content-Length": numBytes,
        "X-Goog-Upload-Header-Content-Type": mimeType,
        "Content-Type": "application/json",
      },
    }
  );

  const uploadUrl = uploadUrlResponse.headers["x-goog-upload-url"];

  const uploadFileResponse = await axios.put(uploadUrl, arrayBuffer, {
    headers: {
      "X-Goog-Upload-Offset": 0,
      "X-Goog-Upload-Command": "upload, finalize",
      "Content-Type": mimeType,
    },
  });

  return uploadFileResponse.data.file.uri;
}

// New Interactive Functions using Raw REST API

import { GoogleGenerativeAI, ChatSession } from "@google/generative-ai";

// Re-export ChatSession type for use in other files
export type { ChatSession };

export async function startGeminiAnalysis(
  fileUri: string,
  mimeType: string,
  roleName: string,
  matter: string
): Promise<{ text: string; chatSession: ChatSession }> {
  // Usa a constante definida no topo do arquivo
  const genAI = new GoogleGenerativeAI(GOOGLE_API_KEY);
  const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

  const prompt = `você é um analista jurídico especialista em direito admininstrativo e financeiro brasileiro. Analise o processo anexo sob a ótica de um "${roleName}" sobre a matéria "${matter}" e faça um breve resumo do que se trata e uma sugestão sobre qual seria o próximo parecer necessário para a melhor análise do processo`;

  const chat = model.startChat();

  // Note: sending fileUri with SDK requires specific format or FileManager? 
  // actually for 'gemini-1.5-flash', it supports fileData parts if passed correctly.

  try {
    const result = await chat.sendMessage([
      { fileData: { fileUri: fileUri, mimeType } },
      { text: prompt }
    ]);

    return {
      text: result.response.text(),
      chatSession: chat
    };
  } catch (error: any) {
    console.error('Gemini SDK Error:', error);
    throw new Error('Erro ao comunicar com o assistente IA: ' + error.message);
  }
}

export async function refineGeminiAnalysis(
  chatSession: ChatSession,
  feedback: string
): Promise<{ text: string; chatSession: ChatSession }> {
  try {
    const result = await chatSession.sendMessage(feedback);
    return {
      text: result.response.text(),
      chatSession // Return same session as it maintains state internally
    };
  } catch (error: any) {
    console.error('Gemini SDK Error:', error);
    throw new Error('Erro ao atualizar a análise: ' + error.message);
  }
}




export async function prepareAndPreAnalyze(
  file: File,
  role: Role,
  matter: string
) {
  const pdfFile = await convertToPdf(file);
  const base64Content = await fileToBase64(pdfFile);
  const geminiUploadUrl = await uploadToGemini(pdfFile);

  let papel = role.name;
  if (role.name === "Parecerista Jurídico" || role.name === "Controlador Interno") {
    papel += " " + matter;
  }

  const { text, chatSession } = await startGeminiAnalysis(geminiUploadUrl, pdfFile.type, papel, matter);

  return {
    preAnalysis: text,
    chatSession,
    base64Content,
    geminiUploadUrl,
    // We return the original role/matter or the refined 'papel'? 
    // submitFinalAnalysis recalculates 'papel', so we just need to pass the original role object later.
  };
}

